import { useState } from 'react';
import PowerSettingsNewIcon from '@material-ui/icons/PowerSettingsNew';
import PlaylistAddCheck from '@material-ui/icons/PlaylistAddCheck';
import ExtensionIcon from '@material-ui/icons/Extension';
import { Menu } from 'common/ui/Menu';
import {
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  List,
  ListSubheader,
  Dialog,
  DialogContent,
  DialogTitle,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import IconButton from '@material-ui/core/IconButton';
import { ReactComponent as SettingIconSVG } from 'assets/settingIcon.svg';
import { CHROME_EXTENSION_INSTALL_PAGE } from 'config/constants';
import { isWorkspaceManagerAvailable } from 'modules/workspace-manager';
import type { PositioningStrategy } from '@popperjs/core/lib/types';
import { useDisplaySystemCheck } from 'common/SystemCheck/useSystemCheck';
import { SironaWorkspaceInfo } from './SironaWorkspaceInfo';

type MenuProfilePopperProps = React.PropsWithChildren<{
  displayName: string | null | undefined;
  onSettingClick?: () => void;
  onLogoutClick: () => void;
  visible?: boolean;
  strategy?: PositioningStrategy;
  showSettingsPane?: boolean;
}>;

export const MenuProfilePopper: React.ComponentType<MenuProfilePopperProps> = ({
  displayName,
  onSettingClick,
  onLogoutClick,
  children,
  visible,
  strategy = 'absolute',
}) => {
  const [showDialog, setShowDialog] = useState(false);
  const { showSystemCheckScreen } = useDisplaySystemCheck();

  const onClose = () => {
    setShowDialog(false);
  };
  return (
    <>
      <Menu
        trigger="click"
        offset={[0, 15]}
        placement="right-start"
        visible={visible}
        strategy={strategy}
        data-testid="menu-profile"
        render={() => (
          <List
            dense
            subheader={<ListSubheader>{displayName}</ListSubheader>}
            component="nav"
            css="width: 21rem"
            data-testid="menu-profile-list"
          >
            <ListItem button onClick={() => setShowDialog(true)}>
              <ListItemText>About Sirona Workspace</ListItemText>
            </ListItem>
            <Divider css="margin: 1rem 0" />
            {onSettingClick && (
              <ListItem button onClick={onSettingClick} data-testid="profile-settings">
                <ListItemIcon>
                  <SettingIconSVG />
                </ListItemIcon>
                <ListItemText>Settings</ListItemText>
              </ListItem>
            )}
            {!isWorkspaceManagerAvailable() && (
              <a
                css="all: unset; display: content;"
                target="_blank"
                rel="noreferrer noopener"
                href={CHROME_EXTENSION_INSTALL_PAGE}
              >
                <ListItem button>
                  <ListItemIcon>
                    <ExtensionIcon />
                  </ListItemIcon>
                  <ListItemText>Install extension</ListItemText>
                </ListItem>
              </a>
            )}
            {onSettingClick && (
              <ListItem button onClick={showSystemCheckScreen}>
                <ListItemIcon>
                  <PlaylistAddCheck />
                </ListItemIcon>
                <ListItemText>System check</ListItemText>
              </ListItem>
            )}
            <ListItem button onClick={onLogoutClick} data-testid="signout">
              <ListItemIcon>
                <PowerSettingsNewIcon />
              </ListItemIcon>
              <ListItemText>Sign out</ListItemText>
            </ListItem>
          </List>
        )}
      >
        {children}
      </Menu>
      <Dialog
        fullWidth
        maxWidth="xs"
        open={showDialog}
        onClose={onClose}
        css={`
          .MuiPaper-root {
            max-width: 398px;
          }
          .MuiDialog-paper {
            padding: 0.5rem 3rem 3rem 3rem;
          }
        `}
      >
        <DialogTitle>
          <IconButton
            aria-label="close"
            css={`
              position: absolute;
              right: 1rem;
              top: 1rem;
            `}
            onClick={onClose}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <SironaWorkspaceInfo />
        </DialogContent>
      </Dialog>
    </>
  );
};
